import React, { useEffect, useMemo } from 'react';
import { ApolloError, gql } from '@apollo/client';
import { Breakpoint, Constrain } from '@moda/om';
import { useLocation } from 'react-router-dom';
import { findRouteFor } from '../../routers';
import { SortOption, DEFAULT_SORT_OPTIONS } from '../../constants';
import { usePLPParams } from '../../hooks/usePLPParams';
import { BackToTop } from '../BackToTop/BackToTop';
import { Loading } from '../Loading';
import { Grid, GridLoading } from '../Grid';
import { Paginator } from '../GridPeripherals/Paginator/Paginator';
import {
  PAGE_FILTER_AVAILABILITY_FACET_FRAGMENT,
  PAGE_FILTER_FRAGMENT,
  PAGE_FILTER_FACET_FRAGMENT,
  PAGE_FILTER_NAME_SLUG_FACET_FRAGMENT,
  PAGE_FILTER_SIZE_FACET_FRAGMENT
} from '../PageFilter';
import { VARIANT_CELL_FRAGMENT, VariantCell } from '../Grid/GridCell/VariantCell/VariantCell';
import { SchemaOrgProductListing, SCHEMA_ORG_PRODUCT_LISTING_FRAGMENT } from '../SchemaOrg';
import { ProductListPageResultFragment } from '../../generated/types/ProductListPageResultFragment';
import { GuidePageQuery_displayPage as DisplayPage } from '../../generated/types/GuidePageQuery';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { compact } from '../../lib/compact';
import { PageMeta } from '../PageMeta';
import { tc } from '../../lib/trackingContext';
import { deferUntilNewTitle } from '../../lib/deferUntilNewTitle';
import { ProductListingDesktop } from './ProductListingDesktop';
import { ProductListingMobile } from './ProductListingMobile';
import { useRedirectLegacyPLPQueryParams } from './useRedirectLegacyPLPQueryParams';
import './ProductListing.scss';
import { ProductListingEmptyState } from './ProductListingEmptyState';

export const NUMBER_OF_ITEMS_PER_PAGE = 60;

export const PRODUCT_LIST_PAGE_VARIANT_EDGE_FRAGMENT = gql`
  fragment ProductListPageVariantEdgeFragment on VariantEdge {
    node {
      ...VariantCellFragment
      ...SchemaOrgProductListingFragment
    }
  }
  ${VARIANT_CELL_FRAGMENT}
  ${SCHEMA_ORG_PRODUCT_LISTING_FRAGMENT}
`;

export const PRODUCT_LIST_PAGE_RESULT_FRAGMENT = gql`
  fragment ProductListPageResultFragment on CollectionResult {
    attributes {
      ...PageFilterNameSlugFacetFragment
    }
    availability {
      ...PageFilterAvailabilityFacetFragment
    }
    designers {
      ...PageFilterNameSlugFacetFragment
    }
    filterColors: filter_colors {
      ...PageFilterFacetFragment
    }
    materials {
      ...PageFilterNameSlugFacetFragment
    }
    saleTags: sale_tags {
      ...PageFilterFacetFragment
    }
    sizes {
      ...PageFilterSizeFacetFragment
    }
    totalItems: total_items
    variants(after: $after) {
      edges {
        ...ProductListPageVariantEdgeFragment
      }
    }
    ...PageFilterFragment
  }
  ${PRODUCT_LIST_PAGE_VARIANT_EDGE_FRAGMENT}
  ${PAGE_FILTER_AVAILABILITY_FACET_FRAGMENT}
  ${PAGE_FILTER_FACET_FRAGMENT}
  ${PAGE_FILTER_NAME_SLUG_FACET_FRAGMENT}
  ${PAGE_FILTER_SIZE_FACET_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
`;

interface ProductListingProps {
  title: string;
  description?: string;
  headerText: string;
  queryResult?: ProductListPageResultFragment | null;
  availableSorts?: SortOption[];
  displayPage?: DisplayPage | null;
  error?: ApolloError;
  loading: boolean;
}

export type ProductListingPath = [string, string];

export const ProductListing: React.FC<ProductListingProps> = ({
  title,
  description,
  headerText,
  queryResult,
  availableSorts = DEFAULT_SORT_OPTIONS,
  displayPage,
  error,
  loading
}) => {
  const {
    params: { page },
    currentSort,
    goToPage,
    changeSort
  } = usePLPParams(availableSorts);

  const variants = useMemo(
    () =>
      compact(
        (queryResult?.variants?.edges || [])
          .filter(edge => !!edge && !!edge.node)
          .map(edge => edge && edge.node)
      ),
    [queryResult]
  );

  const { pathname } = useLocation();
  const route = findRouteFor(pathname);
  const pagetype = route?.pagetype ?? null;

  useEffect(() => {
    deferUntilNewTitle(() => {
      if (variants && variants.length)
        tc.track('Product List Viewed', {
          addToScopeOnce: { products: variants },
          addToTrackingEvent: { pagetype, category: pagetype }
        });
    });
  }, [variants, pagetype]);

  useRedirectLegacyPLPQueryParams();

  if (error) {
    throwQueryError(error);
  }

  if (!queryResult) {
    return <Loading />;
  }

  const totalPages = Math.ceil((queryResult.totalItems || 0) / NUMBER_OF_ITEMS_PER_PAGE);

  const content = (
    <div className="ProductListing__content">
      <Grid className="ProductListing__grid">
        {variants.map(variant => (
          <VariantCell key={variant.id} variant={variant} />
        ))}
      </Grid>

      {queryResult?.totalItems ? (
        <Paginator
          className="ProductListing__pagination"
          totalPages={totalPages}
          selectedPage={page}
          onGoToPage={goToPage}
        />
      ) : (
        <ProductListingEmptyState />
      )}

      <GridLoading show={loading} />
    </div>
  );

  return (
    <>
      <PageMeta title={title} description={description} paginated totalPages={totalPages} />
      <SchemaOrgProductListing variants={variants} />
      <Constrain>
        <div className="ProductListing">
          <Breakpoint gt="md">
            <ProductListingDesktop
              availableSorts={availableSorts}
              currentSort={currentSort}
              headerText={headerText}
              onSortChanged={changeSort}
              queryResult={queryResult}
              displayPage={displayPage}
            >
              {content}
            </ProductListingDesktop>
          </Breakpoint>

          <Breakpoint lt="md">
            <ProductListingMobile
              availableSorts={availableSorts}
              currentSort={currentSort}
              headerText={headerText}
              onSortChanged={changeSort}
              queryResult={queryResult}
              displayPage={displayPage}
            >
              {content}
            </ProductListingMobile>
          </Breakpoint>
        </div>
      </Constrain>
      <BackToTop />
    </>
  );
};
